import { Chess } from "chess.js";

const STORAGE_KEYS = {
  GAMES: "chess_local_games",
  SETTINGS: "chess_settings",
};

const START_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";

const defaultSettings = {
  openai_api_key: null,
  anthropic_api_key: null,
  preferred_provider: "engine",
  preferred_model: null,
  engine_depth: 18,
  stockfish_path: null,
};

const gameDefaults = {
  player_color: "white",
  opponent_name: "Roommate",
  moves: [],
  fen: START_FEN,
  is_player_turn: true,
  status: "active",
  pgn: "",
  suggested_move: null,
  hud_history: [],
  chat_history: [],
  total_tokens: 0,
  total_cost: 0,
};

class ApiError extends Error {
  constructor(status, detail) {
    super(detail);
    this.response = { status, data: { detail } };
  }
}

const nowIso = () => new Date().toISOString();

const makeId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

function getStoredGames() {
  const games = readJson(STORAGE_KEYS.GAMES, []);
  const list = Array.isArray(games) ? games : [];
  const normalized = list.map(normalizeGame).filter(Boolean);
  if (JSON.stringify(list) !== JSON.stringify(normalized)) {
    saveStoredGames(normalized);
  }
  return normalized;
}

function saveStoredGames(games) {
  writeJson(STORAGE_KEYS.GAMES, games);
}

function buildPgn(moves = []) {
  const chess = new Chess();
  for (const move of moves) {
    try {
      chess.move({ from: move.slice(0, 2), to: move.slice(2, 4), promotion: move[4] || "q" });
    } catch {
      return "";
    }
  }
  return chess.pgn();
}

function computeStatus(chess) {
  if (chess.isCheckmate()) return "checkmate";
  if (chess.isStalemate()) return "stalemate";
  if (chess.isInsufficientMaterial()) return "draw";
  if (chess.isDraw()) return "draw";
  return "active";
}

function computeIsPlayerTurn(playerColor, chess) {
  const isWhiteTurn = chess.turn() === "w";
  return (playerColor === "white" && isWhiteTurn) || (playerColor === "black" && !isWhiteTurn);
}

function loadGameOrThrow(gameId) {
  const games = getStoredGames();
  const index = games.findIndex((g) => g.id === gameId);
  if (index === -1) throw new ApiError(404, "Game not found");
  return { games, index, game: games[index] };
}

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

function normalizeGame(raw) {
  if (!raw || typeof raw !== "object") return null;
  const createdAt = raw.created_at || nowIso();
  const moves = Array.isArray(raw.moves) ? raw.moves.filter((m) => typeof m === "string") : [];
  const chatHistory = Array.isArray(raw.chat_history) ? raw.chat_history : [];
  const hudHistory = Array.isArray(raw.hud_history) ? raw.hud_history : [];
  return {
    ...gameDefaults,
    ...raw,
    id: raw.id || makeId(),
    created_at: createdAt,
    updated_at: raw.updated_at || createdAt,
    moves,
    hud_history: hudHistory.slice(0, moves.length),
    chat_history: chatHistory,
    total_tokens: Number(raw.total_tokens || 0),
    total_cost: Number(raw.total_cost || 0),
    opponent_name: raw.opponent_name || "Roommate",
    player_color: raw.player_color === "black" ? "black" : "white",
    fen: raw.fen || START_FEN,
    pgn: typeof raw.pgn === "string" ? raw.pgn : "",
    suggested_move: raw.suggested_move || null,
  };
}

function parsePgnHeaders(pgnText) {
  const headers = {};
  for (const line of (pgnText || "").split(/\r?\n/)) {
    const m = line.match(/^\[(\w+)\s+"(.*)"\]$/);
    if (m) headers[m[1]] = m[2];
  }
  return headers;
}

function buildImportedGame({ pgn, player_color, opponent_name }) {
  const chess = new Chess();
  let loaded = false;
  try {
    const result = chess.loadPgn(pgn);
    loaded = result !== false;
  } catch (e) {
    throw new ApiError(400, `Invalid PGN: ${e.message}`);
  }
  if (!loaded) {
    throw new ApiError(400, "Invalid PGN");
  }

  const verboseHistory = chess.history({ verbose: true }) || [];
  const moves = verboseHistory.map((m) => `${m.from}${m.to}${m.promotion || ""}`);
  const createdAt = nowIso();
  return normalizeGame({
    id: makeId(),
    player_color,
    opponent_name,
    moves,
    fen: chess.fen(),
    is_player_turn: computeIsPlayerTurn(player_color, chess),
    status: computeStatus(chess),
    created_at: createdAt,
    updated_at: createdAt,
    pgn: chess.pgn(),
    suggested_move: null,
    chat_history: [],
    total_tokens: 0,
    total_cost: 0,
  });
}

export const localApi = {
  mode: "local",
  coachProviders: ["engine"],

  async getGames() {
    const games = getStoredGames()
      .slice()
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    return clone(games);
  },

  async getGame(gameId) {
    const { game } = loadGameOrThrow(gameId);
    return clone(game);
  },

  async createGame(input) {
    const createdAt = nowIso();
    const game = {
      id: makeId(),
      player_color: input.player_color,
      opponent_name: input.opponent_name || "Roommate",
      moves: [],
      fen: START_FEN,
      is_player_turn: input.player_color === "white",
      status: "active",
      created_at: createdAt,
      updated_at: createdAt,
      pgn: "",
      suggested_move: null,
      chat_history: [],
      total_tokens: 0,
      total_cost: 0,
    };
    const games = getStoredGames();
    games.unshift(game);
    saveStoredGames(games);
    return clone(game);
  },

  async importPgn(input) {
    const pgn = (input?.pgn || "").trim();
    if (!pgn) throw new ApiError(400, "PGN is empty");

    const headers = parsePgnHeaders(pgn);
    const playerColor = input?.player_color === "black" ? "black" : "white";
    const inferredOpponent =
      playerColor === "white" ? headers.Black || headers.black : headers.White || headers.white;
    const opponentName = (input?.opponent_name || inferredOpponent || "Imported Opponent").trim();

    const game = buildImportedGame({
      pgn,
      player_color: playerColor,
      opponent_name: opponentName,
    });

    const games = getStoredGames();
    games.unshift(game);
    saveStoredGames(games);
    return clone(game);
  },

  async deleteGame(gameId) {
    const games = getStoredGames();
    const next = games.filter((g) => g.id !== gameId);
    if (next.length === games.length) throw new ApiError(404, "Game not found");
    saveStoredGames(next);
    return { message: "Game deleted" };
  },

  async move(gameId, request) {
    const { games, index, game } = loadGameOrThrow(gameId);
    const chess = new Chess(game.fen);

    let result;
    try {
      if (typeof request.move === "string" && /^[a-h][1-8][a-h][1-8][qrbn]?$/i.test(request.move)) {
        result = chess.move({
          from: request.move.slice(0, 2),
          to: request.move.slice(2, 4),
          promotion: request.move[4] || "q",
        });
      } else {
        result = chess.move(request.move);
      }
    } catch (e) {
      throw new ApiError(400, `Invalid move: ${e.message}`);
    }

    if (!result) {
      throw new ApiError(400, "Invalid move");
    }

    const uciMove = `${result.from}${result.to}${result.promotion || ""}`;
    const moves = [...game.moves, uciMove];
    const status = computeStatus(chess);
    const updatedGame = {
      ...game,
      moves,
      fen: chess.fen(),
      is_player_turn: computeIsPlayerTurn(game.player_color, chess),
      status,
      pgn: buildPgn(moves),
      updated_at: nowIso(),
      suggested_move: null,
      hud_history: [],
    };

    games[index] = updatedGame;
    saveStoredGames(games);

    return {
      success: true,
      move: uciMove,
      fen: updatedGame.fen,
      is_player_turn: updatedGame.is_player_turn,
      status: updatedGame.status,
      is_check: chess.isCheck(),
      pgn: updatedGame.pgn,
      hud_history: updatedGame.hud_history,
    };
  },

  async undo(gameId) {
    const { games, index, game } = loadGameOrThrow(gameId);
    if (!game.moves.length) throw new ApiError(400, "No moves to undo");

    const moves = game.moves.slice(0, -1);
    const chess = new Chess();
    for (const move of moves) {
      chess.move({ from: move.slice(0, 2), to: move.slice(2, 4), promotion: move[4] || "q" });
    }

    const updatedGame = {
      ...game,
      moves,
      fen: chess.fen(),
      is_player_turn: computeIsPlayerTurn(game.player_color, chess),
      status: "active",
      pgn: buildPgn(moves),
      updated_at: nowIso(),
      suggested_move: null,
      hud_history: (game.hud_history || []).slice(0, moves.length),
    };

    games[index] = updatedGame;
    saveStoredGames(games);

    return {
      success: true,
      fen: updatedGame.fen,
      is_player_turn: updatedGame.is_player_turn,
      moves_count: moves.length,
      hud_history: updatedGame.hud_history,
    };
  },

  async getHud(gameId, moveIndex) {
    const { games, index, game } = loadGameOrThrow(gameId);
    const targetIndex = typeof moveIndex === "number" ? moveIndex : game.moves.length - 1;
    if (targetIndex < 0 || targetIndex >= game.moves.length) {
      throw new ApiError(400, "Move index out of range");
    }
    const snapshot = (game.hud_history || [])[targetIndex] || null;
    games[index] = game;
    saveStoredGames(games);
    return {
      success: true,
      move_index: targetIndex,
      snapshot,
      hud_history: game.hud_history || [],
      engine: { available: false, name: "Local", depth: 0 },
    };
  },

  async exportPgn(gameId) {
    const { game } = loadGameOrThrow(gameId);
    return { pgn: game.pgn || buildPgn(game.moves) };
  },

  async getSettings() {
    const settings = readJson(STORAGE_KEYS.SETTINGS, null);
    return { ...defaultSettings, ...(settings || {}) };
  },

  async updateSettings(update) {
    const current = await this.getSettings();
    const next = { ...current, ...update };
    writeJson(STORAGE_KEYS.SETTINGS, next);
    return next;
  },

  async coach(gameId, request) {
    throw new ApiError(503, "Engine coach is unavailable in local-only mode. Run the backend with Stockfish.");
  },
};
